<template>
  <div class="table-responsive">
    <table class="table table-sm table-hover align-middle">
      <thead class="bg-light">
        <tr>
          <th class="ps-3 py-3">Client</th>
          <th class="py-3">Order</th>
          <th class="py-3">Proposed Certificates</th>
          <th class="py-3">Confidence</th>
          <th class="pe-3 py-3 text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="match in matches" :key="match.order_id">
          <td class="ps-3">
            <div class="fw-bold text-900">{{ match.company_name }}</div>
            <div class="small text-body-tertiary">
              ID: {{ match.company_id }}
            </div>
          </td>
          <td>
            <div class="fw-semibold text-1000">{{ match.order_mwh }} MWh</div>
            <div class="small text-body-tertiary">(RECEIVE)</div>
          </td>
          <td>
            <div>
              <span class="fw-bold text-900"
                >{{ match.matched_certificates.total_mwh }} MWh</span
              >
              <span
                v-if="match.matched_certificates.technology"
                class="ms-1 text-1000"
              >
                {{ match.matched_certificates.technology }}
              </span>
              <span
                v-if="match.matched_certificates.region"
                class="ms-1 text-body-secondary"
              >
                ({{ match.matched_certificates.region }})
              </span>
            </div>
            <div
              v-if="match.matched_certificates.registry_name"
              class="small text-body-tertiary"
            >
              Registry: {{ match.matched_certificates.registry_name }}
            </div>
            <!-- A match can span well over a thousand certificates (one delivery of
                 6167 MWh arrived as 1420 of them), so the list is capped: printing
                 every id buries the rest of the row. -->
            <div class="small text-body-tertiary">
              {{ match.matched_certificates.certificate_ids.length }}
              certificate(s)
              <span v-if="match.matched_certificates.certificate_ids.length">
                —
                {{
                  summariseCertificateIds(
                    match.matched_certificates.certificate_ids
                  )
                }}
              </span>
            </div>
          </td>
          <td>
            <span
              class="badge"
              :class="getConfidenceBadgeClass(match.confidence)"
            >
              {{ match.confidence }}
            </span>
            <div
              v-if="match.arrival_times?.length"
              class="small text-body-tertiary mt-1"
            >
              {{ describeArrivals(match) }}
            </div>
          </td>
          <td class="pe-3 text-end">
            <div class="btn-group" role="group">
              <button
                class="btn btn-sm btn-outline-success"
                @click="handleConfirm(match)"
                :disabled="isProcessing"
                title="Confirm Match"
              >
                <font-awesome-icon icon="check" class="me-1" />
                Confirm
              </button>
              <button
                class="btn btn-sm btn-outline-danger"
                @click="handleReject(match.order_id)"
                :disabled="isProcessing"
                title="Reject Match"
              >
                <font-awesome-icon icon="times" class="me-1" />
                Reject
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="matches.length === 0">
          <td colspan="5" class="text-center text-muted py-5">
            <div class="mb-2">
              <font-awesome-icon
                icon="check-circle"
                size="2x"
                class="text-body-quaternary"
              />
            </div>
            No suggested matches found
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Orders that cannot be matched exactly yet. Shown read-only: confirming a
         short match would complete the order for less volume than it is owed. -->
    <div v-if="pendingCoverage.length" class="mt-4">
      <h5 class="fw-bold mb-1">Still filling</h5>
      <p class="text-body-secondary small mb-3">
        Certificates arrive every ~15 minutes. These orders do not yet have
        enough unassigned volume in their region to match exactly.
      </p>
      <table class="table table-sm align-middle">
        <thead class="bg-light">
          <tr>
            <th class="ps-3 py-2">Client</th>
            <th class="py-2">Order</th>
            <th class="py-2">Region</th>
            <th class="pe-3 py-2">Available so far</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="coverage in pendingCoverage" :key="coverage.order_id">
            <td class="ps-3">
              <div class="fw-bold text-900">{{ coverage.company_name }}</div>
              <div class="small text-body-tertiary">
                Order #{{ coverage.order_id }}
              </div>
            </td>
            <td class="fw-semibold text-1000">{{ coverage.order_mwh }} MWh</td>
            <td>{{ coverage.region ?? "-" }}</td>
            <td class="pe-3">
              <!-- A fully covered order is not "filling" — showing it at 100% next to a
                   progress bar invites waiting for something that already happened. -->
              <div
                v-if="coverage.ready_to_match"
                class="d-flex align-items-center gap-2"
              >
                <span class="badge bg-success-subtle text-success-emphasis">
                  Ready
                </span>
                <span class="small text-body-secondary">
                  {{ coverage.order_mwh }} MWh available — re-run matching to
                  propose it
                </span>
              </div>
              <div v-else class="d-flex align-items-center gap-2">
                <div class="progress coverage-progress flex-grow-1">
                  <div
                    class="progress-bar bg-warning"
                    :style="{ width: `${coveragePercent(coverage)}%` }"
                  />
                </div>
                <span class="small text-body-secondary">
                  {{ coverage.available_mwh }} / {{ coverage.order_mwh }} MWh
                  ({{ coveragePercent(coverage) }}%)
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <CnmcArrivalReview />
</template>

<script setup lang="ts">
import CnmcArrivalReview from "./CnmcArrivalReview.vue";
import { formatDateTime } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ref } from "vue";
import type {
  MatchResponse,
  PendingOrderCoverage,
} from "./virtualPortfoliosApi";
import { virtualPortfoliosApi } from "./virtualPortfoliosApi";

withDefaults(
  defineProps<{
    matches: MatchResponse[];
    pendingCoverage?: PendingOrderCoverage[];
  }>(),
  { pendingCoverage: () => [] }
);

const emit = defineEmits<{
  "match-confirmed": [];
  "match-rejected": [];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const isProcessing = ref(false);

/**
 * Where the matched certificates came from. A match drawn from a single registry
 * pickup is a stronger signal than one stitched together from several, so the
 * distinction is shown rather than hidden behind the confidence label.
 */
function describeArrivals(match: MatchResponse): string {
  const arrivals = match.arrival_times ?? [];
  if (arrivals.length === 1) {
    return `1 arrival · ${formatDateTime(arrivals[0])}`;
  }
  return `${arrivals.length} arrivals · ${formatDateTime(
    arrivals[0]
  )} – ${formatDateTime(arrivals[arrivals.length - 1])}`;
}

const MAX_SHOWN_CERTIFICATE_IDS = 12;

function summariseCertificateIds(certificateIds: number[]): string {
  if (certificateIds.length <= MAX_SHOWN_CERTIFICATE_IDS) {
    return `#${certificateIds.join(", #")}`;
  }
  const shown = certificateIds.slice(0, MAX_SHOWN_CERTIFICATE_IDS);
  const remaining = certificateIds.length - shown.length;
  return `#${shown.join(", #")} and ${remaining.toLocaleString()} more`;
}

function coveragePercent(coverage: PendingOrderCoverage): number {
  if (coverage.order_mwh <= 0) return 0;
  return Math.min(
    100,
    Math.round((coverage.available_mwh / coverage.order_mwh) * 100)
  );
}

function getConfidenceBadgeClass(confidence: string): string {
  if (confidence.toLowerCase().includes("high")) {
    return "bg-success-subtle text-success-emphasis";
  }
  if (confidence.toLowerCase().includes("medium")) {
    return "bg-warning-subtle text-warning-emphasis";
  }
  return "bg-secondary-subtle text-secondary-emphasis";
}

async function handleConfirm(match: MatchResponse) {
  if (isProcessing.value) return;

  try {
    isProcessing.value = true;
    // matched_at is echoed so the backend can refuse if the suggestion was recomputed
    // between display and click — otherwise a different certificate set than the one
    // reviewed would be assigned.
    await virtualPortfoliosApi.confirmMatch({
      order_id: match.order_id,
      matched_at: match.matched_at ?? null,
    });
    globalSuccessHandler("Success", "Match confirmed successfully");
    emit("match-confirmed");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}

async function handleReject(orderId: number) {
  if (isProcessing.value) return;

  try {
    isProcessing.value = true;
    await virtualPortfoliosApi.rejectMatch({ order_id: orderId });
    globalSuccessHandler("Success", "Match rejected successfully");
    emit("match-rejected");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}
</script>

<style scoped lang="scss">
.coverage-progress {
  height: 6px;
  max-width: 8rem;
}

.table > :not(caption) > * > * {
  padding: 1rem 0.75rem;
}
</style>
